import React, { useState } from 'react';
import { Share2, X, Copy, Check, Loader2 } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../lib/firebase';
import type { AnalysisResult } from '../../types/acquisitionAnalysis';
import { ExecutiveSummary } from './sections/ExecutiveSummary';
import { TrailIncomeYoY } from './sections/TrailIncomeYoY';
import { FYForecast } from './sections/FYForecast';
import { SeasonalityComparison } from './sections/SeasonalityComparison';
import { ValuationSummary } from './sections/ValuationSummary';
import { BrokerPerformance } from './sections/BrokerPerformance';
import { BrokerGrowthTrajectory } from './sections/BrokerGrowthTrajectory';
import { RiskAssessment } from './sections/RiskAssessment';
import { SummaryAssessmentSection } from './sections/SummaryAssessmentSection';
import { MonthlyDataSection } from './sections/MonthlyDataSection';
import { ThirdPartyPayments } from './sections/ThirdPartyPayments';

// Share Modal with copy-to-clipboard functionality
interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

const ShareModal: React.FC<ShareModalProps> = ({ isOpen, onClose, shareUrl, isLoading, error }) => {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
        <div className="flex justify-between items-start mb-4">
          <h3 className="text-lg font-bold text-white">Share Analysis</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            <span className="ml-3 text-gray-300">Generating share link...</span>
          </div>
        )}

        {error && (
          <div className="p-4 bg-red-500/20 border border-red-500/30 rounded-lg mb-4">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {shareUrl && !isLoading && (
          <>
            <p className="text-gray-300 mb-4">
              Share this link with anyone to give them read-only access to this analysis report.
            </p>
            <div className="flex items-center gap-2 mb-6">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white text-sm font-mono"
              />
              <button
                onClick={handleCopy}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg text-white flex items-center gap-2 transition-colors"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </>
        )}

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-700 rounded-md hover:bg-gray-600 transition-colors text-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

interface AnalysisResultViewProps {
  result: AnalysisResult;
  companyId: string;
}

type TabId =
  | 'executive'
  | 'trail-yoy'
  | 'forecast'
  | 'seasonality'
  | 'monthly'
  | 'valuation'
  | 'brokers'
  | 'growth'
  | 'risk'
  | 'third-party'
  | 'summary';

interface TabSpec {
  id: TabId;
  label: string;
  /** When true (default), the tab requires per-broker data and is hidden in entity-only mode. */
  requiresBrokerData?: boolean;
  /** When true, the tab only renders when the result has entity-level (Connective) inputs. */
  entityOnly?: boolean;
}

const TABS: TabSpec[] = [
  { id: 'executive', label: 'Executive Summary' },
  { id: 'trail-yoy', label: 'Trail Income YoY' },
  { id: 'forecast', label: 'FY Forecast' },
  { id: 'seasonality', label: 'Seasonality' },
  { id: 'monthly', label: 'Monthly Data' },
  { id: 'valuation', label: 'Valuation' },
  { id: 'third-party', label: '3rd-Party Payments', entityOnly: true },
  { id: 'brokers', label: 'Broker Performance', requiresBrokerData: true },
  { id: 'growth', label: 'Growth Trajectory', requiresBrokerData: true },
  { id: 'risk', label: 'Risk Assessment' },
  { id: 'summary', label: 'Summary' },
];

export const AnalysisResultView: React.FC<AnalysisResultViewProps> = ({ result, companyId }) => {
  // Default to true for back-compat with existing Loan Market analyses (the
  // backend may omit the field on legacy result documents).
  const brokerOnlyAvailable = result.brokerOnlyAnalysisAvailable !== false;
  const isEntityOnly = !brokerOnlyAvailable;
  const visibleTabs = TABS.filter((tab) => {
    if (tab.requiresBrokerData && isEntityOnly) return false;
    if (tab.entityOnly && !isEntityOnly) return false;
    return true;
  });
  const [activeTab, setActiveTab] = useState<TabId>('executive');
  const [shareModal, setShareModal] = useState<{
    isOpen: boolean;
    shareUrl: string | null;
    isLoading: boolean;
    error: string | null;
  }>({
    isOpen: false,
    shareUrl: null,
    isLoading: false,
    error: null,
  });
  const handleShare = async () => {
    setShareModal({ isOpen: true, shareUrl: null, isLoading: true, error: null });

    try {
      const generateShareTokenFunc = httpsCallable<
        { analysisId: string; companyId: string; expiresInDays?: number },
        { token: string; shareUrl: string }
      >(functions, 'generateShareToken');

      const response = await generateShareTokenFunc({
        analysisId: result.analysisId,
        companyId,
        expiresInDays: 30, // 30-day expiry
      });

      setShareModal({
        isOpen: true,
        shareUrl: response.data.shareUrl,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      console.error('Failed to generate share link:', err);
      setShareModal({
        isOpen: true,
        shareUrl: null,
        isLoading: false,
        error: err.message || 'Failed to generate share link. Please try again.',
      });
    }
  };

  const closeShareModal = () => {
    setShareModal({ isOpen: false, shareUrl: null, isLoading: false, error: null });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'executive':
        return <ExecutiveSummary data={result.executiveSummary} />;
      case 'trail-yoy':
        return <TrailIncomeYoY data={result.trailIncomeYoY} />;
      case 'forecast':
        return <FYForecast data={result.fyForecast} />;
      case 'seasonality':
        return <SeasonalityComparison data={result.seasonality} />;
      case 'monthly':
        return <MonthlyDataSection data={result.monthlyData || []} />;
      case 'valuation':
        return <ValuationSummary data={result.valuationSummary} />;
      case 'third-party':
        return result.entityOnlyAggregate ? (
          <ThirdPartyPayments data={result.entityOnlyAggregate} />
        ) : null;
      case 'brokers':
        return <BrokerPerformance data={result.brokerPerformance} />;
      case 'growth':
        return <BrokerGrowthTrajectory data={result.brokerPerformance.growthTrajectory} />;
      case 'risk':
        return <RiskAssessment data={result.riskAssessment} />;
      case 'summary':
        return <SummaryAssessmentSection data={result.summaryAssessment} />;
      default:
        return null;
    }
  };

  // If the active tab is no longer visible (e.g. we landed on 'brokers' but
  // entity-only just became active for a re-poll), fall back to executive.
  const safeActiveTab = visibleTabs.some((t) => t.id === activeTab)
    ? activeTab
    : 'executive';

  return (
    <div className="space-y-6">
      {/* Header Card */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">{result.targetName}</h2>
            <p className="text-sm text-slate-400">
              Analysis completed on {new Date(result.analysisDate).toLocaleDateString()}
            </p>
            <p className="text-xs text-slate-500">Data period: {result.dataPeriod}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleShare}
              disabled={shareModal.isLoading}
              className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-800 text-white rounded-lg transition-colors flex items-center gap-2"
            >
              {shareModal.isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Share2 className="w-4 h-4" />
              )}
              Share
            </button>
          </div>
        </div>
      </div>

      {/* Entity-only banner */}
      {isEntityOnly && (
        <div className="px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-sm text-amber-200">
          <strong className="text-amber-300">Preliminary entity-level analysis.</strong>{' '}
          Per-broker breakdown is not available for the uploaded source format.
          Top Brokers, Concentration, Retention, and Growth Trajectory tabs are
          hidden. Request a per-broker monthly schedule from the target before
          finalising the deal.
        </div>
      )}

      {/* Tabs */}
      <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 overflow-hidden">
        <div className="border-b border-slate-700/50">
          <div className="flex flex-wrap">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${
                  safeActiveTab === tab.id
                    ? 'border-indigo-500 text-indigo-400 bg-indigo-500/10'
                    : 'border-transparent text-slate-400 hover:text-slate-300 hover:bg-slate-700/30'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">{renderTabContent()}</div>
      </div>

      {/* Share Modal */}
      <ShareModal
        isOpen={shareModal.isOpen}
        onClose={closeShareModal}
        shareUrl={shareModal.shareUrl}
        isLoading={shareModal.isLoading}
        error={shareModal.error}
      />
    </div>
  );
};
